import { FC, useContext, useMemo, useState } from 'react';
import { path } from 'ramda';
import axios from 'axios';
import isNil from 'lodash/isNil';

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import SparkleIcon from 'components/icons/SparkleIcon';
import { IImage } from './ImageSorter';
import { KeycloakContext } from 'components/Secured';
import restEndpoints from 'constants/restEndpoints';
import IProduct from 'models/product';
import ImproveAiPromptModal from './ImproveAiPromptModal';
import { isBlank } from 'utils/helpers-ts';
import GenerateEnvAIModal, { AI_MODEL } from './GenerateEnvAIModal';
import DynamicText from 'components/DynamicText';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  loader: {
    position: 'absolute',
    background: 'rgba(255, 255, 255, 0.9)',
    zIndex: 10,
    padding: theme.spacing(1),
    top: 0,
    left: 0,
    right: 0,
    height: 120 + 16, // image's height + padding
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: '0.938rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    minHeight: '1.5em',
    transition: 'opacity 0.3s ease',
  },
  loadingSpinner: {
    marginBottom: theme.spacing(3),
  },
}));

const IMAGE_IMPROVE_LOADING_TEXTS = [
  'Tar emot bilden...',
  'Analyserar produkten...',
  'Identifierar färger och former...',
  'Läser av text och logotyper...',
  'Planerar för studiokvalitet...',
  'Planerar för optimal belysning...',
  'Genererar förbättrad produktbild...',
  'Applicerar studiobelysning...',
  'Justerar skuggor...',
  'Förbättrar skärpa och detaljer...',
  'Optimerar bildkvalitet...',
  'Skalar upp till hög upplösning...',
  'Tar bort bakgrund...',
  'Finjusterar slutresultatet...',
  'Färdigställer bilden...',
];

// One click: the run log keeps it, and the improved file carries it to its S3 upload, which links the
// run to the image saved with the product.
export const newImproveId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // Not a secure context: fall back below.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

interface AIImproveImpageButtonProps {
  disabled?: boolean;
  initialLoading?: boolean;
  isSuperuser?: boolean;
  model?: string;
  state?: IProduct;
  imageData: IImage;
  onImproved: (file: File) => void;
  onToggleLoading?: (bol: boolean) => void;
}

const AIImproveImpageButton: FC<AIImproveImpageButtonProps> = ({
  disabled,
  initialLoading,
  isSuperuser,
  model: modelProp,
  state,
  imageData,
  onImproved,
  onToggleLoading,
}) => {
  const classes = useStyles();
  const keycloakCtx = useContext(KeycloakContext);
  const token = path(['keycloak', 'token'], keycloakCtx);

  const [loading, setLoading] = useState(!!initialLoading);
  const [openPromptModal, setOpenPromptModal] = useState(false);
  const [openGenerateEnvModal, setOpenGenerateEnvModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const aiModel = useMemo(() => {
    if ([AI_MODEL.gemini, AI_MODEL.chatgpt].includes(modelProp as AI_MODEL)) {
      return modelProp as AI_MODEL;
    }
    return AI_MODEL.sunburst;
  }, [modelProp]);

  const handleUploadLoading = (bol: boolean) => {
    setLoading(bol);
    onToggleLoading?.(bol);
  }

  const handleAiImproveImage = async (
    params?: { prompt?: string, ratio?: string, withEnv?: boolean, model?: AI_MODEL },
  ) => {
    const { prompt, ratio, withEnv, model = aiModel } = params || {};
    handleUploadLoading(true);

    const formData = new FormData();
    const improveId = newImproveId();

    formData.append('model', model);
    formData.append('improveId', improveId);
    formData.append('source', 'product-form');
    if (isSuperuser) {
      if (!isBlank(prompt)) {
        formData.append('customPrompt', prompt || '');
      }
      if (!isBlank(ratio)) {
        formData.append('imageRatio', ratio || '');
      }
      if (!isNil(withEnv)) {
        formData.append('withEnv', String(withEnv));
      }
    }

    try {
      const isLocalImage = !!imageData.imgSrc?.startsWith('blob:');
      formData.append('productId', state?.id || '');
      formData.append('producerId', state?.producerId || '');

      if (isLocalImage) {
        const ext = imageData.pictureUrl?.split('.').pop() || 'png';
        const blob = await fetch(imageData.imgSrc || '').then(res => res.blob());
        const file = new File([blob], `${Date.now()}.${ext}`, { type: blob.type });
        formData.append('file', file);
      } else {
        formData.append('imageUrl', imageData.pictureUrl || '');
      }
      const response = await axios.post(restEndpoints.aiImageImprove, formData, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const fileName = `${Date.now()}_${withEnv ? 'ENVIRONMENT' : 'AIIMPROVED'}.png`;
      const mimeType = response.headers['content-type'] || 'image/png';
      const improvedFile = new File([response.data], fileName, { type: mimeType });

      (improvedFile as any).path = fileName;
      (improvedFile as any).imageImproveId = improveId;

      console.log(improvedFile);
      onImproved(improvedFile);
    } catch {
    } finally {
      handleUploadLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  }

  if (imageData.pictureUrl?.includes('_ENVIRONMENT')) {
    return (
      <Box
        sx={{
          py: 0.75,
          bgcolor: '#E7F1F1',
          color: '#3A6F6E',
          borderRadius: '4px',
          padding: '6px 4px',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ fontWeight: 600 }}>Miljöbild</div>
      </Box>
    );
  }

  if (imageData.pictureUrl?.includes('_AIIMPROVED')) {
    return (
      <Box
        sx={{
          py: 0.75,
          bgcolor: '#E8EAF6',
          color: '#4050B5',
          borderRadius: '4px',
          padding: '6px 4px',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ fontWeight: 600 }}>Förbättrad bild</div>
        <div>Kontrollera resultatet</div>
      </Box>
    );
  }

  return (
    <>
      <Button
        disabled={loading || disabled}
        fullWidth
        size="small"
        variant="contained"
        color="primary"
        onClick={(event) => {
          if (isSuperuser) {
            setAnchorEl(event.currentTarget);
          } else {
            handleAiImproveImage();
          }
        }}
      >
        <SparkleIcon style={{ width: 16 }} />
        <span style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
          {loading ? 'Förbättrar… (ca 1-3 min)' : 'Förbättra'}
        </span>
      </Button>

      {isSuperuser && (
        <>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem
              onClick={() => {
                setOpenPromptModal(true);
                handleClose();
              }}
            >
              Förbättra e-handelsbild
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenGenerateEnvModal(true);
                handleClose();
              }}
            >
              Generera miljöbild
            </MenuItem>
          </Menu>

          {openPromptModal && (
            <ImproveAiPromptModal
              imageData={imageData}
              onSubmit={({ prompt, model }) => handleAiImproveImage({ prompt, model })}
              onClose={() => setOpenPromptModal(false)}
            />
          )}

          {openGenerateEnvModal && (
            <GenerateEnvAIModal
              imageData={imageData}
              onSubmit={({ prompt, model, ratio }) => handleAiImproveImage({ prompt, model, ratio, withEnv: true })}
              onClose={() => setOpenGenerateEnvModal(false)}
            />
          )}
        </>
      )}

      {loading && (
        <div className={classes.loader}>
          <DynamicText
            isRunning
            spinner
            texts={IMAGE_IMPROVE_LOADING_TEXTS}
            spinnerProps={{
              size: 28,
              style: { marginBottom: 16 },
            }}
            style={{ fontSize: 16 }}
          />
        </div>
      )}
    </>
  );
};

export default AIImproveImpageButton;
